import { CheckOutlined, DownOutlined } from '@ant-design/icons'
import { useNotification } from '@refinedev/core'
import { Button, Form, Input, Modal, Select } from 'antd'
import React, { useCallback, useState } from 'react'

import { useCreateCompany, useGetInfoByUserCode } from '../../../../api/company'
import { SmartUidAutoCompleteInput } from '../../../../components/inputs/autocomplete'
import {
  cityRules,
  clearFieldErrorMessageOnChange,
  directorNameRules,
  emailRules,
  nameRules,
  onlyLatinCharsDigitsRule,
  phoneNumberRules,
  postalCodeRules,
  tinRules,
  websiteRules,
} from '../../../../utils/form.constants'
import { useMetadataCountries } from '../../../../utils/hooks/useMetadataCountries'
import { useNotificationOkCancel } from '../../../../utils/hooks/useNotificationOkCancel.hook'
import { ButtonListFooterModalSM } from '../../../components/buttonsList'
import { confirmModalUnsavedChangesProps } from '../../../components/constants'
import {
  companyNameRules,
  companyRegistrationNumberRules,
} from '../../../payment-request/addCompany.constants'

const { TextArea } = Input

interface IProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  customerId?: number
  refetchList?: () => void
}

export const AddRemitterModal: React.FC<IProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  refetchList,
}) => {
  const [form] = Form.useForm()
  const { open: openNotification } = useNotification()
  const [beneficiaryType, setBeneficiaryType] = useState('NEW')
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null)
  const [disableField, setDisableField] = useState(false)
  const [searchCountry, setSearchCountry] = useState<string>('')
  const { metadataCountries } = useMetadataCountries()

  const { mutate: fetchInfoByUserCode, isLoading: isLoadingUserCode } =
    useGetInfoByUserCode({
      onSuccess: (response: { data: { data: any } }) => {
        setInfoByUserCode(response?.data?.data)
        form.setFields([
          {
            name: 'smartUID',
            errors: undefined,
          },
        ])
      },
      onError: (error: { message: string; reason_code: string }) => {
        setInfoByUserCode(
          error?.reason_code === 'ERR00010'
            ? 'This SMART UID does not exist in SMART+'
            : error?.message
        )
        if (error?.reason_code === 'ERR00010') {
          form.setFields([
            {
              name: 'smartUID',
              errors: ['This SMART UID does not exist in SMART+'],
            },
          ])
        } else {
          form.setFields([
            {
              name: 'smartUID',
              errors: [error?.message || ''],
            },
          ])
        }
      },
    })

  const { mutate: createRemitter, isLoading: loadingCreateRemitter } =
    useCreateCompany({
      onSuccess: (response: { data: { message: string } }) => {
        if (openNotification) {
          openNotification({
            key: 'create_success',
            message: response.data.message || 'Create beneficiary and remitter successfully!',
            type: 'success',
          })
        }
        form.resetFields()
        setInfoByUserCode(null)
        setDisableField(false)
        setBeneficiaryType('NEW')
        onClose()
        onSuccess?.()
        refetchList?.()
      },
      onError: (error: { message: string }) => {
        if (openNotification) {
          openNotification({
            key: 'create_failed',
            message: error.message || 'Create beneficiary and remitter failed!',
            type: 'error',
          })
        }
      },
    })

  const handleChangeMethod = (type: string) => {
    setDisableField(false)
    setBeneficiaryType(type)
    setInfoByUserCode(null)
    form?.resetFields()
  }

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value)
  }

  const resetSmartUidLinkedFields = useCallback(() => {
    form?.resetFields([
      'companyName',
      'nickName',
      'directorFirstName',
      'directorLastName',
      'address1',
      'address2',
      'city',
      'stateProvince',
      'companyOfficiallyRegistered',
      'tinVatUid',
      'regNo',
      'phoneNumber1',
      'email',
      'website',
      'postalCode',
    ])
  }, [form])

  const handleSmartUidLookup = useCallback(async (selectedCode?: string) => {
    const code = selectedCode
      ? selectedCode.trim()
      : form.getFieldValue('smartUID')
        ? form.getFieldValue('smartUID')?.trim()
        : ''

    if (!code) {
      setDisableField(false)
      resetSmartUidLinkedFields()
      form.setFields([
        {
          name: 'smartUID',
          errors: ['This field is required'],
        },
      ])
      setInfoByUserCode(null)
      return
    }
    resetSmartUidLinkedFields()
    await fetchInfoByUserCode(code)
  }, [fetchInfoByUserCode, form, resetSmartUidLinkedFields])

  React.useEffect(() => {
    if (
      !infoByUserCode ||
      typeof infoByUserCode === 'string' ||
      beneficiaryType === 'NEW'
    ) {
      setDisableField(false)
      return
    }
    setDisableField(true)
    form.setFieldsValue({
      companyName: infoByUserCode?.fullName,
      address1: infoByUserCode?.address,
      companyOfficiallyRegistered: infoByUserCode?.country,
      regNo: infoByUserCode?.regNo,
      email: infoByUserCode?.email,
      website: infoByUserCode?.website,
    })
  }, [infoByUserCode, beneficiaryType, form])

  const onSubmit = useCallback(
    (values: any) => {
      if (loadingCreateRemitter) return
      const body = {
        fullName: values.companyName || '',
        name: values.nickName || '',
        directorFirstName: values.directorFirstName || '',
        directorLastName: values.directorLastName || '',
        address1: values.address1 || '',
        address2: values.address2 || '',
        city: values.city || '',
        stateProvince: values.stateProvince || '',
        country: values.companyOfficiallyRegistered || '',
        tinVatUid: values.tinVatUid || '',
        regNo: values.regNo || '',
        phoneNumber1: values.phoneNumber1 || '',
        email: values.email || '',
        smartUID: values.smartUID ? values.smartUID?.trim() : undefined,
        website: values.website || '',
        postalCode: values.postalCode || '',
        refUserId: infoByUserCode?.id ? infoByUserCode?.id : undefined,
        userId: customerId,
      }
      createRemitter(body)
    },
    [loadingCreateRemitter, createRemitter, infoByUserCode, customerId]
  )

  const onSave = useCallback(() => {
    form.submit()
  }, [form])

  const { openNotification: openUnsavedNotification, contextHolder } =
    useNotificationOkCancel({
      ...confirmModalUnsavedChangesProps,
      onOk: () => {
        form.resetFields()
        setInfoByUserCode(null)
        setDisableField(false)
        setBeneficiaryType('NEW')
        onClose()
      },
    })

  const onModalCancel = useCallback(() => {
    const formIsNotEmpty = Object.values(form.getFieldsValue()).some(
      (elm) => elm !== undefined && elm !== ''
    )
    if (formIsNotEmpty) {
      openUnsavedNotification()
    } else {
      form.resetFields()
      setInfoByUserCode(null)
      setDisableField(false)
      setBeneficiaryType('NEW')
      onClose()
    }
  }, [onClose, form, openUnsavedNotification])

  return (
    <Modal
      open={open}
      title='Add Beneficiary or Remitter'
      className='smartModalSM'
      onCancel={onModalCancel}
      afterClose={() => {
        form.resetFields()
        setInfoByUserCode(null)
        setDisableField(false)
        setBeneficiaryType('NEW')
      }}
      footer={ButtonListFooterModalSM(
        onSave,
        onModalCancel,
        loadingCreateRemitter || isLoadingUserCode
      )}>
      {contextHolder}
      <Form
        form={form}
        layout='vertical'
        onFinish={onSubmit}
        disabled={loadingCreateRemitter || isLoadingUserCode}
        onValuesChange={clearFieldErrorMessageOnChange(form)}>
        <div
          style={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
            marginBottom: '24px',
            paddingBottom: '16px',
          }}>
          <div className='transfer-mode-list-button'>
            <Button
              onClick={() => handleChangeMethod('NEW')}
              type={beneficiaryType === 'NEW' ? 'primary' : 'default'}
              className={
                beneficiaryType === 'NEW' ? 'smartFooterButtonXS' : undefined
              }
              style={{ marginRight: '8px' }}>
              New
            </Button>

            <Button
              onClick={() => handleChangeMethod('FIND_ON_SMART')}
              type={beneficiaryType === 'FIND_ON_SMART' ? 'primary' : 'default'}
              className={
                beneficiaryType === 'FIND_ON_SMART'
                  ? 'smartFooterButtonXS'
                  : undefined
              }>
              Find on SMART
            </Button>
          </div>
          {beneficiaryType === 'FIND_ON_SMART' ? (
            <Form.Item
              label='SMART UID'
              validateTrigger='onBlur'
              name='smartUID'
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
                {
                  max: 256,
                  message: 'Maximum 256 characters',
                  validateTrigger: 'onChange',
                },
              ]}>
              <SmartUidAutoCompleteInput
                placeholder='Enter the code linked to your SMART account'
                onBlur={() => void handleSmartUidLookup()}
                onSuggestionSelect={(value) => void handleSmartUidLookup(value)}
              />
            </Form.Item>
          ) : null}
        </div>
        <div className='form-info-content'>
          <Form.Item
            label='Company Name'
            name='companyName'
            validateTrigger='onBlur'
            rules={companyNameRules}>
            <Input
              disabled={disableField}
              placeholder='Enter the full legal name of the company'
            />
          </Form.Item>
          <Form.Item
            label='Nickname'
            name='nickName'
            validateTrigger='onBlur'
            rules={[...nameRules, onlyLatinCharsDigitsRule]}>
            <Input placeholder='Enter a short name or alias for internal use' />
          </Form.Item>
          <Form.Item
            label='Reg No'
            name='regNo'
            validateTrigger='onBlur'
            rules={companyRegistrationNumberRules}>
            <Input
              disabled={disableField}
              placeholder='Enter the official company registration number'
            />
          </Form.Item>
          <Form.Item
            label='TIN'
            name='tinVatUid'
            validateTrigger='onBlur'
            rules={tinRules}>
            <Input placeholder="Enter the company's Tax Identification Number" />
          </Form.Item>
          <Form.Item
            label='Address 1'
            name='address1'
            validateTrigger='onBlur'
            rules={[
              onlyLatinCharsDigitsRule,
              { required: true, message: 'This field is required', whitespace: true },
              {
                max: 256,
                message: 'Maximum 256 characters',
                validateTrigger: 'onChange',
              },
            ]}>
            <TextArea
              disabled={disableField}
              className='beneficiary-address'
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder='Enter the primary street address of the company'
            />
          </Form.Item>
          <Form.Item
            label='Address 2'
            name='address2'
            validateTrigger='onBlur'
            rules={[
              onlyLatinCharsDigitsRule,
              {
                max: 256,
                message: 'Maximum 256 characters',
                validateTrigger: 'onChange',
              },
            ]}>
            <TextArea
              className='beneficiary-address'
              autoSize={{ minRows: 1, maxRows: 5 }}
              placeholder='Additional address details (e.g., building, floor)'
            />
          </Form.Item>
          <Form.Item
            label='City'
            name='city'
            validateTrigger='onBlur'
            rules={cityRules}>
            <Input placeholder='Enter the city where the company is located' />
          </Form.Item>
          <Form.Item
            label='State/Province'
            name='stateProvince'
            validateTrigger='onBlur'
            rules={cityRules}>
            <Input placeholder='Enter the state or province of company location' />
          </Form.Item>
          <Form.Item
            label='Country of incorporation'
            name='companyOfficiallyRegistered'
            validateTrigger='onBlur'
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}>
            <Select
              disabled={disableField}
              showSearch
              placeholder='Select the country where the company is officially registered'
              suffixIcon={<DownOutlined />}
              onChange={handleChangeCountry}
              optionLabelProp='label'
              className='select-country-beneficiary'
              filterOption={(input, option) => {
                const label = option?.label
                if (typeof label === 'string') {
                  return label.toLowerCase().includes(input.toLowerCase())
                }
                return false
              }}>
              {metadataCountries.map(
                (item: { value: string; label: string }) => (
                  <Select.Option
                    key={item.value}
                    value={item.value}
                    label={item.label}>
                    <span
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 2,
                      }}>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}>
                        {item.label}
                      </span>
                      {searchCountry === item.value && (
                        <CheckOutlined style={{ flexShrink: 0 }} />
                      )}
                    </span>
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>
          <Form.Item
            label='Postal Code'
            name='postalCode'
            validateTrigger='onBlur'
            rules={postalCodeRules}>
            <Input placeholder='Enter your postal code' />
          </Form.Item>
          <Form.Item
            label="Director's First Name"
            name='directorFirstName'
            validateTrigger='onBlur'
            rules={directorNameRules}>
            <Input placeholder='Enter the first name(s) of the company director' />
          </Form.Item>
          <Form.Item
            label="Director's Last Name"
            name='directorLastName'
            validateTrigger='onBlur'
            rules={directorNameRules}>
            <Input placeholder='Enter the last name (surname) of the company director' />
          </Form.Item>
          <Form.Item
            label='Tel'
            name='phoneNumber1'
            validateTrigger='onBlur'
            rules={phoneNumberRules}>
            <Input placeholder='Enter the official company phone number' />
          </Form.Item>
          <Form.Item
            label='Email'
            name='email'
            validateTrigger='onBlur'
            rules={[
              ...emailRules,
              {
                required: true,
                message: 'This field is required',
                validateTrigger: 'onChange',
              },
            ]}>
            <Input
              disabled={disableField}
              placeholder='Enter the official company email address'
            />
          </Form.Item>
          <Form.Item
            label='Website'
            name='website'
            validateTrigger='onBlur'
            rules={websiteRules}>
            <Input
              disabled={disableField}
              placeholder="Enter the company's official website URL"
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  )
}
